import React from 'react';
import { Badge } from './ui/badge';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { 
  BadgeCheck, ShieldCheck, Camera, CreditCard, ClipboardCheck, 
  FileText, Scale, Lock, Shield, Eye
} from 'lucide-react';

/**
 * TrustBadge Component
 * 
 * A reusable trust indicator badge for displaying platform protection features.
 * 
 * @param {string} type - Badge type: 'verified-host', 'id-verified', 'evidence-backed', 
 *                        'secure-payments', 'inspection-required', 'agreement-stored',
 *                        'admin-review', 'bond-protected'
 * @param {string} size - Badge size: 'sm', 'md', 'lg'
 * @param {boolean} showTooltip - Whether to show tooltip on hover
 * @param {boolean} showLabel - Whether to show the label text
 * @param {string} className - Additional CSS classes
 */
const TrustBadge = ({ 
  type, 
  size = 'sm', 
  showTooltip = false, 
  showLabel = true,
  className = '' 
}) => {
  // Badge configurations
  const badgeConfigs = {
    'verified-host': {
      icon: BadgeCheck,
      label: 'Verified Host',
      tooltip: 'This host has completed identity verification.',
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      iconColor: 'text-emerald-600'
    },
    'id-verified': {
      icon: ShieldCheck,
      label: 'ID Verified',
      tooltip: 'Identity has been verified through the platform.',
      color: 'bg-green-50 text-green-700 border-green-200',
      iconColor: 'text-green-600'
    },
    'evidence-backed': {
      icon: Camera,
      label: 'Evidence-Backed',
      tooltip: 'All trips include documented inspections and photo evidence.',
      color: 'bg-blue-50 text-blue-700 border-blue-200',
      iconColor: 'text-blue-600'
    },
    'secure-payments': {
      icon: CreditCard,
      label: 'Secure Payment',
      tooltip: 'Payments processed securely.',
      color: 'bg-purple-50 text-purple-700 border-purple-200',
      iconColor: 'text-purple-600'
    },
    'inspection-required': {
      icon: ClipboardCheck,
      label: 'Inspection Required',
      tooltip: 'Pre-hire and post-hire inspections are required for every trip.',
      color: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      iconColor: 'text-cyan-600'
    },
    'agreement-stored': {
      icon: FileText,
      label: 'Agreement Stored',
      tooltip: 'Booking agreements are generated and stored for this trip.',
      color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      iconColor: 'text-indigo-600'
    },
    'admin-review': {
      icon: Scale,
      label: 'Admin Review',
      tooltip: 'Disputes are reviewed by the platform admin team.',
      color: 'bg-amber-50 text-amber-700 border-amber-200',
      iconColor: 'text-amber-600'
    },
    'bond-protected': {
      icon: Shield,
      label: 'Bond Protected',
      tooltip: 'Security bond may be held for this rental.',
      color: 'bg-slate-50 text-slate-700 border-slate-200',
      iconColor: 'text-slate-600'
    }
  };

  const config = badgeConfigs[type];
  if (!config) return null;

  const IconComponent = config.icon;

  // Size configurations
  const sizeClasses = {
    sm: {
      badge: 'px-2 py-0.5 text-xs',
      icon: 'w-3 h-3',
      gap: 'gap-1'
    },
    md: {
      badge: 'px-2.5 py-1 text-xs',
      icon: 'w-3.5 h-3.5',
      gap: 'gap-1.5'
    },
    lg: {
      badge: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4',
      gap: 'gap-2'
    }
  };

  const sizeConfig = sizeClasses[size] || sizeClasses.sm;

  const BadgeContent = (
    <Badge 
      className={`
        ${config.color} 
        ${sizeConfig.badge} 
        ${sizeConfig.gap}
        inline-flex items-center border font-medium
        ${className}
      `}
      data-testid={`trust-badge-${type}`}
    >
      <IconComponent className={`${sizeConfig.icon} ${config.iconColor} flex-shrink-0`} />
      {showLabel && <span>{config.label}</span>}
    </Badge>
  );

  if (showTooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {BadgeContent}
          </TooltipTrigger>
          <TooltipContent 
            side="top" 
            className="max-w-[200px] text-center text-xs"
          >
            <p>{config.tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return BadgeContent;
};

/**
 * TrustBadgeGroup Component
 * 
 * Displays a group of trust badges in a flex wrap layout.
 * 
 * @param {Array} badges - Array of badge type strings or config objects
 * @param {string} size - Size for all badges
 * @param {boolean} showTooltips - Whether to show tooltips
 * @param {string} className - Additional CSS classes for container
 */
export const TrustBadgeGroup = ({ 
  badges = [], 
  size = 'sm', 
  showTooltips = false,
  className = '' 
}) => {
  if (!badges || badges.length === 0) return null;

  return (
    <div 
      className={`flex flex-wrap gap-1.5 ${className}`}
      data-testid="trust-badge-group"
    >
      {badges.map((badge, index) => {
        const type = typeof badge === 'string' ? badge : badge.type;
        return (
          <TrustBadge 
            key={`${type}-${index}`}
            type={type}
            size={size}
            showTooltip={showTooltips}
          />
        );
      })}
    </div>
  );
};

/**
 * TrustSection Component
 * 
 * A section displaying trust features with descriptions.
 * Used on vehicle detail pages and booking screens.
 * 
 * @param {string} title - Section title
 * @param {Array} features - Array of badge types to display with descriptions
 * @param {boolean} compact - Whether to use compact layout
 * @param {string} className - Additional CSS classes
 */
export const TrustSection = ({ 
  title = 'Trust & Safety',
  features = ['verified-host', 'evidence-backed', 'secure-payments', 'inspection-required'],
  compact = false,
  className = '' 
}) => {
  const badgeConfigs = {
    'verified-host': {
      icon: BadgeCheck,
      label: 'Verified Host',
      description: 'This host has completed identity verification.',
      color: 'bg-emerald-50 border-emerald-100',
      iconColor: 'text-emerald-600',
      iconBg: 'bg-emerald-100'
    },
    'id-verified': {
      icon: ShieldCheck,
      label: 'ID Verified',
      description: 'Identity verified through the platform.',
      color: 'bg-green-50 border-green-100',
      iconColor: 'text-green-600',
      iconBg: 'bg-green-100'
    },
    'evidence-backed': {
      icon: Camera,
      label: 'Evidence-Backed Rental',
      description: 'All trips require documented inspections and photo evidence.',
      color: 'bg-blue-50 border-blue-100',
      iconColor: 'text-blue-600',
      iconBg: 'bg-blue-100'
    },
    'secure-payments': {
      icon: CreditCard,
      label: 'Secure Payments',
      description: 'Payments processed securely.',
      color: 'bg-purple-50 border-purple-100',
      iconColor: 'text-purple-600',
      iconBg: 'bg-purple-100'
    },
    'inspection-required': {
      icon: ClipboardCheck,
      label: 'Inspection Required',
      description: 'Pre-hire and post-hire inspections are required for every trip.',
      color: 'bg-cyan-50 border-cyan-100',
      iconColor: 'text-cyan-600',
      iconBg: 'bg-cyan-100'
    },
    'agreement-stored': {
      icon: FileText,
      label: 'Booking Agreement Stored',
      description: 'Booking agreements are generated and stored for each trip.',
      color: 'bg-indigo-50 border-indigo-100',
      iconColor: 'text-indigo-600',
      iconBg: 'bg-indigo-100'
    },
    'admin-review': {
      icon: Scale,
      label: 'Admin Dispute Review',
      description: 'Disputes are reviewed by the platform admin team.',
      color: 'bg-amber-50 border-amber-100',
      iconColor: 'text-amber-600',
      iconBg: 'bg-amber-100'
    },
    'bond-protected': {
      icon: Shield,
      label: 'Bond Protection',
      description: 'Security bond may be held to cover potential damages.',
      color: 'bg-slate-50 border-slate-100',
      iconColor: 'text-slate-600',
      iconBg: 'bg-slate-100'
    }
  };

  if (compact) {
    return (
      <div className={`${className}`} data-testid="trust-section-compact">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-[#004B8D]" />
          <span className="text-sm font-medium text-slate-700">{title}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {features.map((type, index) => {
            const config = badgeConfigs[type];
            if (!config) return null;
            return (
              <TrustBadge 
                key={`${type}-${index}`}
                type={type}
                size="sm"
                showTooltip={true}
              />
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`} data-testid="trust-section">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#004B8D]/10 flex items-center justify-center">
          <ShieldCheck className="w-4 h-4 text-[#004B8D]" />
        </div>
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {features.map((type, index) => {
          const config = badgeConfigs[type];
          if (!config) return null;
          const IconComponent = config.icon;
          return (
            <div 
              key={`${type}-${index}`}
              className={`${config.color} border rounded-xl p-3 flex items-start gap-3`}
            >
              <div className={`w-8 h-8 rounded-lg ${config.iconBg} flex items-center justify-center flex-shrink-0`}>
                <IconComponent className={`w-4 h-4 ${config.iconColor}`} />
              </div>
              <div>
                <p className="font-medium text-slate-900 text-sm">{config.label}</p>
                <p className="text-xs text-slate-600 mt-0.5">{config.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * TripProtectionBadges Component
 * 
 * A compact trust indicator for booking flows.
 */
export const TripProtectionBadges = ({ className = '' }) => {
  return (
    <div className={`bg-slate-50 rounded-xl p-4 ${className}`} data-testid="trip-protection-badges">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-[#004B8D]" />
        <span className="text-sm font-medium text-slate-700">Trip Protection</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <TrustBadge type="evidence-backed" size="sm" showTooltip={true} />
        <TrustBadge type="agreement-stored" size="sm" showTooltip={true} />
        <TrustBadge type="secure-payments" size="sm" showTooltip={true} />
        <TrustBadge type="admin-review" size="sm" showTooltip={true} />
      </div>
    </div>
  );
};

export default TrustBadge;
